import React from 'react';

type UseCountUpFormatter = (value: number) => string;

export interface UseCountUpOptions {
    start?: number;
    end: number;
    duration?: number;
    prefix?: string;
    suffix?: string;
    formatter?: UseCountUpFormatter;
}

const defaultFormatter: UseCountUpFormatter = (value) => Math.round(value).toString();

export const useCountUp = ({
    start = 0,
    end,
    duration = 2000,
    prefix = '',
    suffix = '',
    formatter = defaultFormatter
}: UseCountUpOptions) => {

    const formatValue = React.useCallback(
        (numericValue: number) => `${prefix}${formatter(numericValue)}${suffix}`,
        [formatter, prefix, suffix]
    );

    const [displayValue, setDisplayValue] = React.useState(() => formatValue(start));

    React.useEffect(() => {
        if (typeof window === 'undefined') {
            setDisplayValue(formatValue(end));
            return;
        }

        const startTimestamp = performance.now();
        const range = end - start;

        let animationFrameId: number;

        const step = (timestamp: number) => {
            const elapsed = timestamp - startTimestamp;
            const progress = duration > 0 ? Math.min(elapsed / duration, 1) : 1;
            const currentValue = start + range * progress;

            setDisplayValue(formatValue(currentValue));

            if (progress < 1) {
                animationFrameId = window.requestAnimationFrame(step);
            }
        };

        animationFrameId = window.requestAnimationFrame(step);

        return () => {
            if (animationFrameId) {
                window.cancelAnimationFrame(animationFrameId);
            }
        };
    }, [duration, end, formatValue, start]);

    return displayValue;
};

export default useCountUp;

